import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Pill } from '@feathr/components';

import type { IAdGroupKeyword } from '../AdStep.types';
import { getMatchTypeLabel } from './KeywordFields.utils';

import * as styles from './KeywordChip.css';

interface IKeywordChipProps {
  keyword: IAdGroupKeyword;
  index: number;
  onRemove: (index: number) => void;
  /** Negative keywords carry no match type, so it isn't rendered for them. */
  showMatchType?: boolean;
  /** Whether this keyword has a problem. A chip is not a field, so it can only tint. */
  hasError?: boolean;
  disabled?: boolean;
}

/**
 * One keyword as a removable chip — the whole chip is the remove control, the same
 * interaction as the Location step's `LocationTag`. A keyword with a problem is tinted
 * red; the message itself is listed above, since a chip has nowhere to put one.
 *
 * @param props - {@link IKeywordChipProps}.
 */
function KeywordChip({
  keyword,
  index,
  onRemove,
  showMatchType = false,
  hasError = false,
  disabled = false,
}: Readonly<IKeywordChipProps>): JSX.Element {
  const { t } = useTranslation();

  function handleRemove(): void {
    onRemove(index);
  }

  const label = showMatchType
    ? `${keyword.text} · ${getMatchTypeLabel(keyword.matchType, t)}`
    : keyword.text;

  return (
    <Button
      className={styles.root}
      disabled={disabled}
      onClick={handleRemove}
      tooltip={t('Remove')}
      type={'naked'}
    >
      <Pill
        showPrefix={false}
        suffix={faXmark}
        theme={hasError ? 'red' : 'neutral'}
        truncate={false}
      >
        {label}
      </Pill>
    </Button>
  );
}

export default KeywordChip;
